import { Link, Outlet } from "react-router-dom";

import { T, useT } from "@repo/transifex";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useUpdateTill } from "~/api/tills/hooks/use-update-till";
import { type ReceiptPrintMode } from "~/api/tills/types";
import {
  Button,
  DialogError,
  DialogLoading,
  SelectCreatable,
  SettingsLayout,
  Switch
} from "~/components";
import { useSendWebEvent } from "~/hooks";
import { useAppContext } from "~/providers/app";
import { extractScaleIdentifier } from "~/utils/hardware/scale";
import { toast } from "~/components/hooks/use-toast";
import { FREEDOMPAY_RECEIPT_FORMAT } from "~/utils/constants";
import { logger } from "~/services/logger";

import { ConnectionGuide } from "./ConnectionGuide";
import { FreedomPayStatusBadge } from "./freedompay-status-badge";
import { SelectCustomShopperName } from "./SelectCustomShopperName";
import { ConfigItem } from "./config-item";
import { DeviceConnect } from "./device-connect";
import { ConfigItemLanguage } from "./config-item-language";
import { ConfigItemProductColors } from "./config-item-product-colors";

export const DevicesPage = () => {
  const {
    hardware: {
      printer,
      scales: {
        scale,
        model: scaleModel,
        unit: scaleUnit,
        decimals: scaleDecimals
      },
      freedompayPoi,
      freedompayStatus
    }
  } = useAppContext();

  const t = useT();

  const allPrintModeOptions: { value: ReceiptPrintMode; label: string }[] = [
    { value: "automatic", label: t("Automatic") },
    { value: "manual", label: t("Manual") },
    { value: "disabled", label: t("Disabled") }
  ];

  const { data: till, status: tillStatus } = useCurrentTill();

  const sendWebEvent = useSendWebEvent();

  const { mutate: updateTill } = useUpdateTill();

  const handleOpenSettings = () => {
    if (till?.paymentGatewayConfig?.provider !== "freedompay") {
      logger.info("Not opening FreedomPay settings", {
        till_id: till?.id,
        payment_gateway_config: till?.paymentGatewayConfig,
        freedompay_poi_information: !freedompayPoi
          ? undefined
          : {
              name: freedompayPoi?.name,
              connectionType: freedompayPoi?.connectionType
            }
      });
      toast({
        variant: "destructive",
        title: t("Invalid FreedomPay Configuration"),
        description: t("Payment gateway configuration is not valid")
      });
      return;
    }
    const config = till.paymentGatewayConfig;
    if (!config.storeId) {
      return toast({
        title: t("Invalid FreedomPay Configuration"),
        description: t("Missing Store ID"),
        variant: "destructive"
      });
    }

    if (!config.terminalId) {
      return toast({
        title: t("Invalid FreedomPay Configuration"),
        description: t("Missing Terminal ID"),
        variant: "destructive"
      });
    }

    if (!config.esKey) {
      return toast({
        title: t("Invalid FreedomPay Configuration"),
        description: t("Missing ES Key"),
        variant: "destructive"
      });
    }

    if (!config.activationKey) {
      return toast({
        title: t("Invalid FreedomPay Configuration"),
        description: t("Missing Activation Key"),
        variant: "destructive"
      });
    }

    sendWebEvent({
      type: "FREEDOMPAY_OPEN_SETTINGS_REQUEST",
      payload: {
        tillId: till.id,
        storeId: config.storeId,
        terminalId: config.terminalId,
        esKey: config.esKey,
        activationKey: config.activationKey,
        currencyISOCode: till.currency,
        languageCode: till.language,
        receiptFormat: FREEDOMPAY_RECEIPT_FORMAT[till.country] || "FRANCE"
      }
    });

    logger.info("Opening FreedomPay settings", {
      store_id: config.storeId,
      terminal_id: config.terminalId,
      freedompay_poi_information: !freedompayPoi
        ? undefined
        : {
            name: freedompayPoi?.name,
            connectionType: freedompayPoi?.connectionType
          }
    });
  };

  if (tillStatus === "pending") {
    return (
      <SettingsLayout title={t("Device settings")}>
        <DialogLoading />
      </SettingsLayout>
    );
  }

  if (tillStatus === "error") {
    return (
      <SettingsLayout title={t("Device settings")}>
        <DialogError backUrl=".." />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout title={t("Settings")}>
      <div className="flex w-full flex-col gap-10">
        <ConfigItemLanguage />

        <ConfigItem
          title={<T _str="Categories" />}
          description={
            <T _str="Enable the 'ALL' category in the header of the Sell view" />
          }
          allowedTillTypes={["attended", "self-service", "collect"]}
        >
          {(till) => (
            <Switch
              checked={till.config.showAllCategory}
              handleChange={(enabled) => {
                updateTill({
                  showAllCategory: enabled
                });
              }}
            />
          )}
        </ConfigItem>

        <ConfigItemProductColors />

        <ConfigItem
          title={<T _str="Barcoded Products" />}
          description={
            <T _str="Hide products with barcodes from the sell page" />
          }
          subtitle={
            <T _str="When enabled, only non-barcoded products will be shown, making them easier to find. Barcoded products remain scannable at all times." />
          }
          allowedTillTypes={["attended", "self-service", "collect", "kiosk"]}
        >
          {(till) => (
            <Switch
              checked={till.config?.barcodedProductsDisplayMode === "hidden"}
              handleChange={(enabled) => {
                updateTill({
                  barcodedProductsDisplayMode: enabled ? "hidden" : "visible"
                });
              }}
            />
          )}
        </ConfigItem>

        <ConfigItem
          title={<T _str="Payment terminal" />}
          allowedTillTypes={["attended", "self-service", "kiosk", "collect"]}
          hidden={
            till.paymentGatewayConfig?.provider !== "adyen" &&
            till.paymentGatewayConfig?.provider !== "freedompay" &&
            (!till.paymentProviders.includes("card") ||
              !till.paymentProviders.includes("swish"))
          }
        >
          {(till) => {
            if (till.paymentGatewayConfig?.provider === "adyen") {
              return (
                <DeviceConnect
                  identifier={till.terminalId}
                  connectRoute="/settings/devices/terminals"
                  onDisconnect={() => {
                    updateTill({ terminalId: null });
                  }}
                />
              );
            }

            if (till.paymentGatewayConfig?.provider === "freedompay") {
              return (
                <div className="flex w-full items-center justify-between">
                  <div className="flex min-w-0 items-center gap-2">
                    <FreedomPayStatusBadge status={freedompayStatus} />
                    {freedompayPoi?.name ? (
                      <span className="truncate text-base text-text-primary">
                        {freedompayPoi.name}
                      </span>
                    ) : null}
                  </div>

                  <Button onClick={handleOpenSettings}>
                    <T _str="Open Settings" />
                  </Button>
                </div>
              );
            }

            return null;
          }}
        </ConfigItem>

        <ConfigItem
          title={<T _str="Cash drawer | Printer" />}
          subtitle={!printer ? <ConnectionGuide /> : null}
          allowedTillTypes={[
            "attended",
            "self-service",
            "kiosk",
            "collect",
            "simple-checkout",
            "ticket-validation"
          ]}
        >
          {() => (
            <DeviceConnect
              identifier={
                printer
                  ? `${printer.interfaceType} - ${printer.identifier} - ${printer.model}`
                  : null
              }
              onDisconnect={() => {
                if (printer) {
                  sendWebEvent({
                    type: "PRINTER_DISCONNECT_REQUEST",
                    payload: printer
                  });
                }
              }}
              connectRoute="/settings/devices/printers"
              onConnect={() => {
                sendWebEvent({ type: "PRINTER_DISCOVER_REQUEST" });
              }}
            />
          )}
        </ConfigItem>

        <ConfigItem
          title={<T _str="Scale" />}
          allowedTillTypes={["attended", "self-service", "collect", "kiosk"]}
        >
          {() => (
            <DeviceConnect
              identifier={
                scale
                  ? `${extractScaleIdentifier(scale)} - ${scaleUnit}${
                      scaleDecimals === null
                        ? ""
                        : `, ${scaleDecimals} decimals`
                    }`
                  : null
              }
              connectRoute="/settings/devices/scales"
              secondaryAction={
                // The precision check needs to know the model to open the
                // scale; a pairing from a wrapper that never reported one has
                // to be redone first.
                scaleModel ? (
                  <Link to="/settings/devices/scales/precision">
                    <Button variant="secondary">
                      <T _str="Configure precision" />
                    </Button>
                  </Link>
                ) : null
              }
              onConnect={() => {
                sendWebEvent({
                  type: "SCALE_DISCOVER_REQUEST"
                });
              }}
              onDisconnect={() => {
                sendWebEvent({
                  type: "SCALE_REMOVE"
                });
              }}
            />
          )}
        </ConfigItem>

        <ConfigItem
          title={<T _str="Card reader / Scanner" />}
          allowedTillTypes={["self-service"]}
        >
          {(till) => (
            <Switch
              checked={till.config.hasEmployeeCardReader}
              handleChange={(enabled) => {
                updateTill({
                  employeeCardReader: enabled
                });
              }}
            />
          )}
        </ConfigItem>

        <ConfigItem
          title={<T _str="Custom shopper name" />}
          allowedTillTypes={["kiosk"]}
        >
          {(till) => (
            <SelectCustomShopperName value={till.config.customShopperName} />
          )}
        </ConfigItem>

        {till.preCheckoutTextInputs?.length ? (
          <ConfigItem
            title={<T _str="Dining options" />}
            description={
              <T _str="Reset dining option after every transaction" />
            }
            allowedTillTypes={["attended"]}
          >
            {(till) => (
              <Switch
                checked={till.config.resetDiningOption}
                handleChange={(enabled) => {
                  updateTill({
                    resetDiningOption: enabled
                  });
                }}
              />
            )}
          </ConfigItem>
        ) : null}

        <ConfigItem
          title={<T _str="Print Mode" />}
          description={<T _str="Select the print mode for the system" />}
          allowedTillTypes={[
            "simple-checkout",
            "self-service",
            "kiosk",
            "ticket-validation"
          ]}
        >
          {(till) => {
            const options =
              till.type === "ticket-validation"
                ? allPrintModeOptions.filter((o) => o.value !== "manual")
                : allPrintModeOptions;
            const receiptPrintMode = till.config?.receiptPrintMode;

            return (
              <SelectCreatable
                className="!text-base"
                onChange={(selected) => {
                  updateTill({
                    receiptPrintMode: selected?.value
                  });
                }}
                options={options}
                placeholder={t("Choose a print mode")}
                value={
                  receiptPrintMode
                    ? {
                        value: receiptPrintMode,
                        label: t(
                          {
                            automatic: "Automatic",
                            manual: "Manual",
                            disabled: "Disabled"
                          }[receiptPrintMode]
                        )
                      }
                    : null
                }
              />
            );
          }}
        </ConfigItem>

        <ConfigItem
          title={<T _str="Checkout settings" />}
          description={<T _str="Skip receipt page" />}
          allowedTillTypes={["attended", "collect"]}
        >
          {(till) => (
            <Switch
              checked={till.config.skipReceiptPage}
              handleChange={(enabled) => {
                updateTill({
                  skipReceiptPage: enabled
                });
              }}
            />
          )}
        </ConfigItem>
      </div>

      <Outlet />
    </SettingsLayout>
  );
};
